<template>
  <ion-header>
    <ion-toolbar :style="contentStyles">
      <ion-buttons slot="start">
        <ion-back-button />
      </ion-buttons>
      <ion-title>{{ $t('order.controls') }}</ion-title>
    </ion-toolbar>
  </ion-header>
  <ion-content ref="scrollview" :fullscreen="true" :style="contentStyles">
    <AppStack direction="flex-col" flex="flex-1" class="min-h-full pt-[10px]">
      <AppStack
        v-if="loading"
        key="loading"
        align-items="items-center"
        justify-content="justify-center"
        flex="flex-1"
      >
        <ion-spinner
          :style="{ '--color': 'var(--rf-fg-elevation-0-primary)' }"
        />
      </AppStack>
      <AppStack
        v-else-if="order && orderMenuItem && menuItem"
        direction="flex-col pt-[10px]"
      >
        <AppStack
          v-for="group in accountNavigation"
          :key="group.key"
          direction="flex-col gap-1"
        >
          <AppStack class="px-4 bg-bg-elevation-0 pt-[6px] pb-[6px]">
            <AppTypography
              :text="group.label"
              name="text-xs-4-semibold"
              color="text-fg-elevation-1-ios-secondary"
              class="uppercase"
            />
          </AppStack>
          <ion-list inset class="!my-0">
            <AppPageViewListItem
              v-for="(item, index) in group.items"
              :key="group.key + String(index)"
              :title="item.title"
              :button="item.button"
              :accent="item.accent || undefined"
              elevation="1"
              @click="item?.onClick"
            />
          </ion-list>
          <div class="pt-4" />
        </AppStack>
      </AppStack>
      <AppStack
        v-else
        align-items="items-center"
        justify-content="justify-center"
        flex="flex-1"
      >
        <AppTypography
          :text="$t('order.notLoaded')"
          name="text-sm-4-medium"
          color="text-fg-elevation-1-ios-secondary"
        />
      </AppStack>
    </AppStack>
  </ion-content>
</template>

<script lang="ts">
export default { name: 'OrderModalMenuItemControls' }
</script>

<script setup lang="ts">
import {
  IonButtons,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonBackButton,
  IonList,
  IonSpinner,
  toastController,
} from '@ionic/vue'
import { ref, computed, inject, ComputedRef, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useBottomScroll } from '@restify/packages/composables/useBottomScroll'
import useAppEventBus from '~/composables/useAppEventBus'
import AppStack from '@restify/packages/design-system/low-level/AppStack.vue'
import AppTypography from '@restify/packages/design-system/low-level/AppTypography.vue'
import AppPageViewListItem from '~/components/AppPageViewListItem.vue'
import { useIonicNavigation } from '@restify/packages/composables/useIonicNavigation'
import useStores from '~/composables/useStores'

const { pop } = useIonicNavigation('order-modal-nav')
const { t } = useI18n()
const appBus = useAppEventBus()
const injectedOrderId = inject<ComputedRef<string>>('orderId')

const props = defineProps<{
  id?: string | null
  menuItemId?: string | null
}>()

const { orders: OrdersStore, 'menu-items': MenuItemsStore } = useStores()

const loading = ref(false)
const scrollview = ref(null)
const scrollviewContent = ref(null)

useBottomScroll(scrollview, scrollviewContent)

const contentStyles = computed(() => ({
  '--background': 'var(--rf-bg-elevation-0)',
  '--border-color': 'var(--rf-border-elevation-0-primary)',
}))

const order = computed(() => {
  if (!injectedOrderId?.value) return

  return OrdersStore.getFromStore(injectedOrderId.value).value
})

const orderMenuItem = computed(() => {
  if (!order.value) return

  return order.value?.menuItems.find((item) => item._id === props.id)
})

// The check is frozen once it enters checkout — see the remove guard.
const isOrderLocked = computed(() =>
  ['checking-out', 'checked-out'].includes(order.value?.status?.name ?? ''),
)

const menuItem = computed(() => {
  if (!props.menuItemId) return

  return MenuItemsStore.getFromStore(props.menuItemId).value
})

type NavItem = {
  button: boolean
  title: string
  accent: 'blue' | 'red'
  onClick: () => unknown
}
type NavGroup = { key: string; label: string; items: NavItem[] }

const accountNavigation = computed<NavGroup[]>(() => {
  if (!menuItem.value || !orderMenuItem.value || !order.value) return []

  const onClick = (status: string) => () => {
    if (!order.value || !orderMenuItem.value) return {}

    OrdersStore.patch(
      order.value._id,
      {
        $set: {
          [`menuItems.$.status.name`]: status,
          [`menuItems.$.status.${status}At`]: Date.now(),
        },
      },
      {
        query: {
          'menuItems._id': orderMenuItem.value._id,
        },
      },
    )
      .then(() => pop())
      .catch(catchErrorWithToast)
  }

  const statusItems: NavItem[] = []
  const status = orderMenuItem.value.status.name

  if (status === 'idle') {
    statusItems.push({
      button: false,
      title: t('order.controlsPage.markAsInProgress'),
      accent: 'blue',
      onClick: onClick('inProgress'),
    })
  }
  if (status === 'inProgress') {
    statusItems.push({
      button: false,
      title: t('order.controlsPage.markAsReady'),
      accent: 'blue',
      onClick: onClick('ready'),
    })
  }
  if (status === 'ready') {
    statusItems.push({
      button: false,
      title: t('order.controlsPage.markAsServed'),
      accent: 'blue',
      onClick: onClick('served'),
    })
  }
  if (status !== 'cancelled') {
    statusItems.push({
      button: false,
      title: t('order.controlsPage.markAsCancelled'),
      accent: 'red',
      // Cancelling drops the item from the billable total — same
      // anti-fraud concern as remove. Block it once checkout started.
      onClick: isOrderLocked.value
        ? () => showLockedToast()
        : onClick('cancelled'),
    })
  }

  const actionItems: NavItem[] = [
    {
      button: false,
      title: t('order.controlsPage.remove'),
      accent: 'red',
      onClick: () => {
        if (!order.value || !orderMenuItem.value) return {}

        // Anti-fraud invariant: once the order is being checked out a
        // receipt may already be printed for it. Removing an item now
        // would let the printed total exceed what's actually in the
        // order (and orphans the item on any unpaid transaction, which
        // then can't be fiscalised). Block it with a clear message.
        if (isOrderLocked.value) {
          showLockedToast()

          return {}
        }

        return OrdersStore.patch(order.value._id, {
          $pull: {
            [`menuItems`]: {
              _id: orderMenuItem.value._id,
            },
          },
        })
          .then(() => pop())
          .catch(catchErrorWithToast)
      },
    },
  ]

  const groups: NavGroup[] = []
  if (statusItems.length) {
    groups.push({
      key: 'status',
      label: t('order.controlsPage.groups.status'),
      items: statusItems,
    })
  }
  if (actionItems.length) {
    groups.push({
      key: 'actions',
      label: t('order.controlsPage.groups.actions'),
      items: actionItems,
    })
  }

  return groups
})

const catchErrorWithToast = async (error: any) => {
  const toast = await toastController.create({
    message: t('order.errorPrefix', {
      name: error.name,
      message: error.message,
    }),
    duration: 3000,
    cssClass: 'toast text-lg-7-semibold',
    position: 'bottom',
    mode: 'ios',
  })

  await toast.present()
}

const showLockedToast = async () => {
  const toast = await toastController.create({
    message: t('order.controlsPage.removeLockedInCheckout'),
    duration: 3000,
    cssClass: 'toast text-lg-7-semibold',
    position: 'bottom',
    mode: 'ios',
  })

  await toast.present()
}

const getMenuItem = (silent = false) => {
  loading.value = silent ? false : true

  if (!props.menuItemId) return

  return MenuItemsStore.get(props.menuItemId).finally(
    () => (loading.value = false),
  )
}

appBus.on((event) => {
  if (event.type === 'app-visible') {
    return getMenuItem(true)
  }
})

onMounted(() => {
  return getMenuItem()
})
</script>
